import { useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, FileText, Target, Calendar, Youtube, Check, Loader2 } from "lucide-react";
import logo from '../../assets/images/envelope-artie.png';
import podcastIcon from '../../assets/images/podcast.png';
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useOnboarding } from "@/context/OnboardingContext";
import { toast } from "sonner";
import { useEffect, useState } from "react";
import { useAuthStore } from "@/stores/authStore";
import { apiClient } from "@/lib/api";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { renderAsync } from 'docx-preview';
import { useRef } from "react";

export default function NewProjectStep3() {
    const navigate = useNavigate();
    const { data, updateData } = useOnboarding();
    const { user } = useAuthStore();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [scriptData, setScriptData] = useState<any>(null);
    const [isLoadingScript, setIsLoadingScript] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const docxContainerRef = useRef<HTMLDivElement>(null);

    // Hydrate from URL if possible (Robustness)
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const urlProjectId = params.get('projectId');

        if (urlProjectId && !data.projectId) {
            // If we have ID in URL but not context, try to recover
            updateData({ projectId: urlProjectId });
        }
    }, []);

    useEffect(() => {
        // Basic validation - relaxed to allow async loading
        if ((!data.projectTitle || !data.projectType || !data.budget) && !data.projectId) {
            // Only redirect if we effectively have NOTHING (no ID, no data)
            // toast.error("Please complete all project details before proceeding");
        }

        // Redirect if script file is missing (e.g. after refresh) and we haven't uploaded it yet
        if (!data.scriptFile && !data.scriptId && !data.projectId) {
            toast.error("Script file missing. Please re-upload.");
            navigate('/projects/create/step-2');
        }
    }, [data, navigate]);

    // Manage local preview URL for file object
    useEffect(() => {
        if (showPreview && data.scriptFile) {
            const isDocx = data.scriptFile.name.toLowerCase().endsWith('.docx');

            if (isDocx) {
                // For DOCX, we don't need a URL, we'll render the blob directly
                // But we wait for the dialog to open and ref to be available
                setTimeout(() => {
                    if (docxContainerRef.current && data.scriptFile) {
                        docxContainerRef.current.innerHTML = ''; // Clear previous content
                        renderAsync(data.scriptFile, docxContainerRef.current)
                            .then(() => console.log("DOCX rendered successfully"))
                            .catch(err => console.error("Error rendering DOCX:", err));
                    }
                }, 100); // Small delay to ensure Dialog is rendered
            } else {
                const url = URL.createObjectURL(data.scriptFile);
                setPreviewUrl(url);
                return () => {
                    URL.revokeObjectURL(url);
                    setPreviewUrl(null);
                };
            }
        }
    }, [showPreview, data.scriptFile]);

    // Fetch script data if we have a scriptId (for existing projects) or projectId
    useEffect(() => {
        const fetchScript = async () => {
            let sid = data.scriptId;
            // Also check URL params as a backup source for projectId
            const params = new URLSearchParams(window.location.search);
            const pid = data.projectId || params.get('projectId');

            setIsLoadingScript(true);

            try {
                // ALWAYS try to fetch scripts if we have a projectId, to ensure we have the latest
                // This covers cases where context might be stale or missing the script ID
                if (pid) {
                    try {
                        const scripts = await apiClient.getScriptsByProject(pid);
                        if (scripts && scripts.length > 0) {
                            const latestScript = scripts[0];
                            sid = latestScript.id || latestScript._id; // Ensure we get the ID correctly

                            // Always update context with the latest found script to ensure UI sync
                            updateData({
                                scriptId: sid,
                                scriptFileName: latestScript.filename
                            });

                            // Also ensure project details are loaded if missing
                            if (!data.projectTitle) {
                                const project = await apiClient.getProject(pid);
                                updateData({
                                    projectId: project._id,
                                    projectTitle: project.title,
                                    projectType: project.format,
                                    logline: project.logline || ""
                                });
                            }
                        }
                    } catch (e) {
                        console.error("Scripts/Project fetch failed", e);
                    }
                }

                // If we found a script ID (either from context or lookup), fetch its full data
                if (sid) {
                    const response = await apiClient.getScript(sid);
                    setScriptData(response);
                    // Double-ensure context has it if we just fetched it
                    if (!data.scriptId) {
                        updateData({ scriptId: sid });
                    }
                }
            } catch (error) {
                console.error("Error fetching script data:", error);
            } finally {
                setIsLoadingScript(false);
            }
        };
        fetchScript();
    }, [data.projectId]); // Run when projectId changes (or mounts) - removed data.scriptId to avoid loops/stale closure

    const getProjectTypeDisplay = () => {
        if (!data.projectType) return "Project Type";

        if ((data.projectType === "Vertical short" || data.projectType === "Long-form episodic") && data.episodeCount) {
            return `${data.projectType} (${data.episodeCount} episode${data.episodeCount !== 1 ? 's' : ''})`;
        }

        return data.projectType;
    };

    const parseBudget = (str: string, projectType: string = "") => {
        if (!str || str === "I'm not sure") {
            switch (projectType) {
                case "Vertical short": return 35000;
                case "Long-form episodic": return 175000;
                case "Short film": return 175000;
                case "Feature film":
                case "Other":
                default: return 500000;
            }
        }
        const matches = str.match(/(\d+(?:\.\d+)?)\s*[kKmM]?/g);
        if (!matches) return 0;
        let maxVal = 0;
        matches.forEach(m => {
            const num = parseFloat(m);
            if (isNaN(num)) return;
            let multiplier = 1;
            const lower = m.toLowerCase();
            if (lower.includes('k')) multiplier = 1000;
            if (lower.includes('m')) multiplier = 1000000;
            const val = num * multiplier;
            if (val > maxVal) maxVal = val;
        });
        return maxVal;
    };

    const handleNext = async () => {
        if (!data.projectTitle || !data.projectType || !data.budget) {
            toast.error("Please complete all project details before proceeding");
            return;
        }

        try {
            setIsSubmitting(true);

            let projectId = data.projectId;
            let scriptId = data.scriptId;
            const budgetValue = parseBudget(data.budget, data.projectType);

            // 1. Create or Update Project
            let formatValue = data.projectType;
            if (data.projectType === "Long-form episodic" || data.projectType === "Vertical short") {
                formatValue = `${data.projectType} (${data.episodeCount} episode${data.episodeCount !== 1 ? 's' : ''})`;
            }

            if (projectId) {
                await apiClient.updateProject(projectId, {
                    title: data.projectTitle,
                    format: formatValue,
                    episode_count: data.episodeCount,
                    brand_revenue_target: budgetValue,
                    logline: data.logline
                });
            } else {
                // Fallback: Create if somehow missing
                const projectPayload = {
                    title: data.projectTitle,
                    status: "draft",
                    brand_revenue_target: budgetValue,
                    target_30d_reach: 0,
                    launch_date: new Date().toISOString().split('T')[0],
                    created_via_onboarding: false,
                    format: formatValue,
                    episode_count: data.episodeCount,
                    logline: data.logline
                };
                const createdProject = await apiClient.createProject(projectPayload);
                // @ts-ignore
                projectId = createdProject.id || createdProject._id;
            }

            if (!projectId) throw new Error("Failed to process project");

            // 2. Upload Script OR Trigger Analysis
            if (data.scriptFile && (data.scriptFile !== scriptData?.file || !scriptId)) {
                // Case A: New file to upload. Triggers analysis automatically via 'and_calculate_score: true'
                const scriptResult = await apiClient.uploadScript({
                    projectId: projectId,
                    file: data.scriptFile,
                    title: data.scriptFile.name,
                    and_calculate_score: true
                });
                // @ts-ignore
                scriptId = scriptResult.script_id || scriptResult.id || scriptResult._id;
            } else if (scriptId) {
                // Case B: Script already uploaded (e.g. Resume from draft).
                // We MUST trigger analysis explicitly because 'uploadScript' wasn't called.
                await apiClient.calculateArtieScore(projectId, true);
            }

            // 3. Update Context with real IDs
            updateData({
                projectId,
                scriptId
            });

            // 4. Navigate to loading page, replacing history to remove step-3
            navigate('/projects/create/loading', { replace: true });

        } catch (error) {
            console.error("Failed to start analysis:", error);
            toast.error("Failed to start analysis. Please try again.");
            setIsSubmitting(false);
        }
    };

    const handleBack = () => {
        if (data.sameAsPrior) {
            // Skips Audience/Team
            navigate(`/projects/create/step-2?projectId=${data.projectId}`);
        } else {
            // Full Flow: Step 2 -> Audience -> Team -> Step 3
            navigate('/projects/create/team');
        }
    };

    // Helper to get user's social info (reused from OnboardingV2Page3 logic roughly)
    // We are skipping the explicit "social input" step for New Project as per instructions
    // "take the social media information (for youtube data or any other social, using 'me' api - it'd already have everything in it)"
    const getSocialStatus = () => {
        if (data.sameAsPrior) {
            // If user opted to use prior project's settings, display social data from their user profile.
            return {
                youtube: !!user?.youtube_data,
                instagram: user?.social_profiles?.find(p => p.platform === "instagram")?.handle,
                tiktok: user?.social_profiles?.find(p => p.platform === "tiktok")?.handle,
                spotify: user?.social_profiles?.find(p => p.platform === "spotify")?.handle,
                applePodcast: user?.social_profiles?.find(p => p.platform === "apple_podcasts")?.handle,
            };
        } else {
            // If user went through the audience step, only show what was connected/entered in this specific flow.
            const justConnectedYoutube = sessionStorage.getItem("project_connecting_youtube") === "true";
            return {
                youtube: !!user?.youtube_data && justConnectedYoutube,
                instagram: data.instagram,
                tiktok: data.tiktok,
                spotify: data.spotify,
                applePodcast: data.applePodcast,
            };
        }
    };

    const socials = getSocialStatus();

    return (
        <div className="min-h-screen selection:bg-[oklch(0.70_0.18_55)] selection:text-[oklch(1_0_0)] relative bg-[oklch(1.0000_0_0)] text-[oklch(0.2686_0_0)] overflow-x-hidden flex flex-col items-center font-sans">
            <main className="relative z-10 w-full flex flex-col items-center min-h-screen">

                <div className="w-full max-w-3xl px-6 py-12 flex flex-col items-center flex-grow">
                    {/* Logo */}
                    <div className="mb-8 relative">
                        <div className="absolute inset-0 bg-[oklch(0.97_0.01_60)] blur-xl opacity-50"></div>
                        <img
                            src={logo}
                            alt="Artie"
                            className="w-48 h-48 object-contain relative z-10"
                        />
                    </div>

                    {/* Header Section */}
                    <div className="text-center max-w-2xl mb-10 animate-in slide-in-from-bottom-4 duration-500">
                        <h1 className="text-4xl md:text-5xl font-bold mb-4 font-cabinet tracking-tight text-[oklch(0.2686_0_0)]">
                            All set. Here’s what <br /> I’ll review.
                        </h1>
                        <p className="text-lg text-[oklch(0.55_0_0)] font-ibm leading-relaxed mb-2 max-w-[700px] mx-auto">
                            This helps me give you a thoughtful early read. You can edit anything before I start.
                        </p>
                    </div>

                    <div className="w-full space-y-6 animate-in slide-in-from-bottom-4 duration-500 delay-100">
                        {/* Summary Card */}
                        <Card className="w-full bg-white shadow-xl border border-[oklch(0.92_0_0)] overflow-hidden p-0 rounded-[12px]">
                            <div className="p-8 pb-6">
                                <div className="flex flex-col md:flex-row gap-8">
                                    {/* Project Section - Left Column */}
                                    <div className="flex-1 space-y-4">
                                        <div>
                                            <h3 className="text-sm font-bold text-[oklch(0.55_0_0)] uppercase tracking-wider mb-1">Project</h3>
                                            <h2 className="text-2xl font-bold text-[oklch(0.2686_0_0)]">{data.projectTitle || "Untitled Project"}</h2>
                                            <p className="text-[oklch(0.55_0_0)]">{getProjectTypeDisplay()}</p>
                                        </div>

                                        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-[oklch(0.96_0_0)]">
                                            <div className="p-2 bg-white rounded-md border border-[oklch(0.92_0_0)]">
                                                <FileText className="w-5 h-5 text-[oklch(0.55_0_0)]" />
                                            </div>
                                            <div className="flex-1">
                                                <p className="text-sm font-semibold text-[oklch(0.2686_0_0)]">
                                                    {scriptData?.title || data.scriptFileName || "No script uploaded"}
                                                </p>
                                                {scriptData?.page_count && (
                                                    <p className="text-xs text-[oklch(0.55_0_0)]">{scriptData.page_count} pages</p>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Script Preview - Right Column */}
                                    <div
                                        className={`w-full md:w-32 bg-gray-50 rounded-lg border border-[oklch(0.96_0_0)] flex items-center justify-center min-h-[140px] ${(scriptData?.storage_path || data.scriptFile || data.scriptId) ? 'cursor-pointer hover:bg-gray-100 transition-colors' : ''}`}
                                        onClick={() => {
                                            if (scriptData?.storage_path || data.scriptFile || data.scriptId) {
                                                setShowPreview(true);
                                            }
                                        }}
                                    >
                                        <div className="text-center">
                                            {isLoadingScript ? (
                                                <Loader2 className="w-8 h-8 text-[oklch(0.55_0_0)] mx-auto mb-2 animate-spin" />
                                            ) : (
                                                <FileText className={`w-8 h-8 ${(scriptData?.storage_path || data.scriptFile || data.scriptId) ? 'text-[oklch(0.2686_0_0)]' : 'text-[oklch(0.85_0_0)]'} mx-auto mb-2`} />
                                            )}
                                            <span className={`text-xs ${(scriptData?.storage_path || data.scriptFile || data.scriptId) ? 'text-[oklch(0.2686_0_0)] font-bold' : 'text-[oklch(0.55_0_0)] font-medium'}`}>
                                                {isLoadingScript ? 'Loading...' : (scriptData?.storage_path || data.scriptFile || data.scriptId) ? 'Preview Script' : 'No Script'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="h-px bg-[oklch(0.92_0_0)] mb-8 mx-8"></div>

                            <div className="p-8 pt-2">
                                {/* Audience Section */}
                                <div className="mb-8">
                                    <h3 className="text-sm font-bold text-[oklch(0.2686_0_0)] mb-4">Audience</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        {/* Column 1: Social Connected */}
                                        <div className="space-y-3">
                                            {socials.youtube && (
                                                <div className="flex items-center gap-2 text-[oklch(0.55_0_0)]">
                                                    <Youtube className="w-4 h-4 shrink-0 text-red-600" />
                                                    <span className="text-sm truncate max-w-[120px]">Connected</span>
                                                    <div className="w-4 h-4 rounded-full bg-black flex items-center justify-center ml-1 shrink-0">
                                                        <Check className="w-2.5 h-2.5 text-white" />
                                                    </div>
                                                </div>
                                            )}
                                            {socials.instagram && (
                                                <div className="flex items-center gap-2 text-[oklch(0.55_0_0)]">
                                                    <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" fill="#E1306C" />
                                                    </svg>
                                                    <span className="text-sm truncate max-w-[120px]">{socials.instagram}</span>
                                                    <div className="w-4 h-4 rounded-full bg-black flex items-center justify-center ml-1 shrink-0">
                                                        <Check className="w-2.5 h-2.5 text-white" />
                                                    </div>
                                                </div>
                                            )}
                                            {socials.tiktok && (
                                                <div className="flex items-center gap-2 text-[oklch(0.55_0_0)]">
                                                    <svg className="w-4 h-4 shrink-0 text-black" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
                                                    </svg>
                                                    <span className="text-sm truncate max-w-[120px]">{socials.tiktok}</span>
                                                    <div className="w-4 h-4 rounded-full bg-black flex items-center justify-center ml-1 shrink-0">
                                                        <Check className="w-2.5 h-2.5 text-white" />
                                                    </div>
                                                </div>
                                            )}
                                            {socials.spotify && (
                                                <div className="flex items-center gap-2 text-[oklch(0.55_0_0)]">
                                                    <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="#1DB954" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141 4.32-1.38 9.841-.719 13.44 1.56.42.24.6.841.301 1.261zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z" />
                                                    </svg>
                                                    <span className="text-sm truncate max-w-[120px]">{socials.spotify}</span>
                                                    <div className="w-4 h-4 rounded-full bg-black flex items-center justify-center ml-1 shrink-0">
                                                        <Check className="w-2.5 h-2.5 text-white" />
                                                    </div>
                                                </div>
                                            )}
                                            {socials.applePodcast && (
                                                <div className="flex items-center gap-2 text-[oklch(0.55_0_0)]">
                                                    <img
                                                        src={podcastIcon}
                                                        alt="Apple Podcast"
                                                        className="w-4 h-4 shrink-0 object-contain"
                                                    />
                                                    <span className="text-sm truncate max-w-[120px]">{socials.applePodcast}</span>
                                                    <div className="w-4 h-4 rounded-full bg-black flex items-center justify-center ml-1 shrink-0">
                                                        <Check className="w-2.5 h-2.5 text-white" />
                                                    </div>
                                                </div>
                                            )}
                                            {!socials.youtube && !socials.instagram && !socials.tiktok && !socials.spotify && !socials.applePodcast && (
                                                <div className="flex items-center gap-2 text-[oklch(0.55_0_0)]">
                                                    <span className="text-sm italic">No social profiles connected</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="h-px bg-[oklch(0.92_0_0)] mb-8"></div>

                                {/* Brand Support Goal Section */}
                                <div>
                                    <h3 className="text-sm font-bold text-[oklch(0.2686_0_0)] mb-4">Project Budget</h3>
                                    <div className="flex items-center gap-4">
                                        <Target className="w-8 h-8 text-[oklch(0.55_0_0)] stroke-1" />
                                        <div>
                                            <div className="text-2xl font-bold text-[oklch(0.2686_0_0)]">{data.budget || "$0"}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Card>

                        {/* Timeline Card */}
                        <Card className="w-full bg-white shadow-xl border border-[oklch(0.92_0_0)] overflow-hidden p-8 rounded-[12px]">
                            <h3 className="text-lg font-bold text-[oklch(0.2686_0_0)] mb-6">What happens next</h3>

                            <div className="space-y-8 relative">
                                {/* Step 1 */}
                                <div className="flex gap-4">
                                    <div className="w-8 h-8 rounded-full bg-[oklch(0.2686_0_0)] text-white flex items-center justify-center font-bold text-sm shrink-0">1</div>
                                    <div className="space-y-1.5">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="font-bold text-[oklch(0.2686_0_0)]">Initial assessment begins immediately</span>
                                            <span className="px-2 py-0.5 rounded-md bg-[oklch(0.96_0_0)] text-[oklch(0.2686_0_0)] text-xs font-medium border border-[oklch(0.92_0_0)]">1-2 minutes</span>
                                        </div>
                                        <p className="text-sm text-[oklch(0.55_0_0)] leading-relaxed">
                                            I'll analyze your script and audience signals to estimate what your project could earn from brand partnerships. You'll see initial insights within minutes.
                                        </p>
                                    </div>
                                </div>

                                {/* Step 2 */}
                                <div className="flex gap-4">
                                    <div className="w-8 h-8 rounded-full bg-[oklch(0.2686_0_0)] text-white flex items-center justify-center font-bold text-sm shrink-0">2</div>
                                    <div className="space-y-1.5">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="font-bold text-[oklch(0.2686_0_0)]">Professional review follows</span>
                                            <span className="px-2 py-0.5 rounded-md bg-[oklch(0.96_0_0)] text-[oklch(0.2686_0_0)] text-xs font-medium border border-[oklch(0.92_0_0)]">3-5 business days</span>
                                        </div>
                                        <p className="text-sm text-[oklch(0.55_0_0)] leading-relaxed">
                                            Our AI works with WGA writers and brand strategists to assess craft and market fit - giving you insights to sharpen your commercial positioning. This part takes a little longer, but it's worth it.
                                        </p>
                                    </div>
                                </div>

                                {/* Step 3 */}
                                <div className="flex gap-4">
                                    <div className="w-8 h-8 rounded-full bg-[oklch(0.2686_0_0)] text-white flex items-center justify-center font-bold text-sm shrink-0">3</div>
                                    <div className="space-y-1.5">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="font-bold text-[oklch(0.2686_0_0)]">Packaging and next steps</span>
                                        </div>
                                        <p className="text-sm text-[oklch(0.55_0_0)] leading-relaxed">
                                            I'll pull everything together into actionable recommendations to best position your project for commercial success. Expect to spend a few hours over 1-2 weeks on things like documenting your production plan, growing your audience, and assembling what brands need to say yes.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Callout Box */}
                            <div className="mt-8 bg-[oklch(0.98_0_0)] rounded-xl p-4 flex items-start gap-4 border border-[oklch(0.92_0_0)]">
                                <div className="w-10 h-10 rounded-full bg-white border border-[oklch(0.92_0_0)] flex items-center justify-center shrink-0 shadow-sm">
                                    <Calendar className="w-5 h-5 text-[oklch(0.2686_0_0)]" />
                                </div>
                                <div>
                                    <p className="font-bold text-[oklch(0.2686_0_0)] text-sm mb-1">First matching window closes soon</p>
                                    <p className="text-xs text-[oklch(0.55_0_0)] leading-normal">Complete your packaging steps by then to be eligible for our first round of brand matching.</p>
                                </div>
                            </div>
                        </Card>
                    </div>

                </div>

                {/* Footer & Navigation */}
                <div className="w-full mt-auto pb-10 px-6 md:px-20">
                    <div className="w-full border-t border-[oklch(0.92_0_0)] mb-8" />
                    <div className="w-full flex items-center justify-between">
                        <div className="flex-1 flex justify-start">
                            <Button
                                variant="ghost"
                                className="text-[oklch(0.55_0_0)] hover:text-[oklch(0.2686_0_0)] hover:bg-transparent pl-0"
                                onClick={handleBack}
                            >
                                <ArrowLeft className="w-4 h-4 mr-2" />
                                Back
                            </Button>
                        </div>

                        {/* Progress Indicators */}
                        <div className="flex gap-2 justify-center">
                            <div className="w-2.5 h-2.5 rounded-full bg-[oklch(0.92_0_0)]"></div>
                            <div className="w-2.5 h-2.5 rounded-full bg-[oklch(0.92_0_0)]"></div>
                            <div className="w-2.5 h-2.5 rounded-full bg-[oklch(0.92_0_0)]"></div>
                            <div className="w-2.5 h-2.5 rounded-full bg-[oklch(0.2686_0_0)]"></div>
                        </div>

                        <div className="flex-1 flex justify-end">
                            <Button
                                className="bg-black hover:bg-black/90 text-white rounded-xl px-8 h-12 shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                onClick={handleNext}
                                disabled={isSubmitting}
                            >
                                Get an early read
                                <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                        </div>
                    </div>
                </div>

            </main>

            {/* Script Preview Modal */}
            <Dialog open={showPreview} onOpenChange={setShowPreview}>
                <DialogContent className="max-w-6xl h-[90vh] p-0 flex flex-col gap-0 outline-none">
                    <div className="px-6 py-4 border-b bg-white shrink-0">
                        <h3 className="text-lg font-semibold">{data.scriptFileName || scriptData?.filename || 'Script Preview'}</h3>
                    </div>
                    <div className="flex-1 overflow-y-auto bg-gray-100 p-4 min-h-0">
                        {(data.scriptFile?.name.toLowerCase().endsWith('.docx')) ? (
                            <div ref={docxContainerRef} className="bg-white shadow-lg min-h-full p-8 mb-8" />
                        ) : (
                            (previewUrl || data.scriptId) && (
                                <iframe
                                    src={previewUrl || `${import.meta.env.VITE_API_BASE_URL}/api/v1/scripts/${data.scriptId}/view#toolbar=0&navpanes=0&scrollbar=1`}
                                    className="w-full h-full border-0"
                                    title="Script Preview"
                                />
                            )
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}